import { useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';

import { useApiClient, useHasPermission, useToastService } from '@@common';
import { AgentClient, AgentModel, FileClient } from '../../api/api';
import { Permissions } from '../../common/Permissions';
import AgentAvatar from './AgentAvatar';

interface AgentDialogProps {
	open: boolean;
	agent?: AgentModel;
	onClose: () => void;
	onSuccess: () => void;
}

export default function AgentDialog({ open, agent, onClose, onSuccess }: AgentDialogProps) {
	const client = useApiClient(AgentClient);
	const fileClient = useApiClient(FileClient);
	const canCreateFiles = useHasPermission(Permissions.FileCreate);
	const canReadFiles = useHasPermission(Permissions.FileRead);
	const canUploadAvatar = canCreateFiles && canReadFiles;
	const toastService = useToastService();
	const [name, setName] = useState(agent?.name ?? '');
	const [fileId, setFileId] = useState<string | null>(agent?.fileId ?? null);
	const [image, setImage] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string>();
	const [imageError, setImageError] = useState<string>();
	const isEditing = agent !== undefined;

	useEffect(() => {
		setImage(null);
		setImageError(undefined);
		if (open) {
			setName(agent?.name ?? '');
			setFileId(agent?.fileId ?? null);
		}
	}, [agent, open]);
	useEffect(() => {
		if (!image) {
			setPreviewUrl(undefined);
			return;
		}
		const url = URL.createObjectURL(image);
		setPreviewUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [image]);

	const mutation = useMutation({
		mutationFn: async () => {
			const value = name.trim();
			if (!value) {
				throw new Error('Name is required');
			}
			let avatarFileId = fileId;
			if (image) {
				const uploaded = await fileClient.upload(undefined, { data: image, fileName: image.name }, JSON.stringify({ Purpose: 'AgentAvatar' }));
				avatarFileId = uploaded.id;
				setFileId(uploaded.id);
				setImage(null);
			}
			if (agent) {
				await client.updateAgent(agent.id, { name: value, fileId: avatarFileId ?? undefined });
			} else {
				await client.createAgent({ name: value, fileId: avatarFileId ?? undefined });
			}
		},
		onSuccess: () => {
			toastService.success(isEditing ? 'Agent updated' : 'Agent created');
			onSuccess();
		},
		onError: (error) => toastService.error(`Agent could not be saved: ${error instanceof Error ? error.message : 'Unknown error'}`),
	});

	return (
		<Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
			<DialogTitle>{isEditing ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
			<DialogContent>
				<Stack direction="row" alignItems="center" spacing={2} sx={{ my: 1 }}>
					<AgentAvatar name={name} fileId={image ? undefined : fileId} previewUrl={previewUrl} size={64} />
					<Stack spacing={0.5}>
						<Stack direction="row" spacing={1}>
							<Button component="label" size="small" startIcon={<PhotoCamera />} disabled={mutation.isPending || !canUploadAvatar}>
								{fileId || image ? 'Change avatar' : 'Upload avatar'}
								<input type="file" aria-label="Agent avatar image" hidden accept="image/png,image/jpeg,image/gif,image/webp" disabled={mutation.isPending || !canUploadAvatar} onChange={event => {
									const selected = event.target.files?.[0];
									event.target.value = '';
									if (!selected) return;
									if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(selected.type) || selected.size > 5 * 1024 * 1024) {
										setImageError('Choose a PNG, JPEG, GIF or WebP image of at most 5 MB.');
										return;
									}
									setImageError(undefined);
									setImage(selected);
								}} />
							</Button>
							{(fileId || image) && <Button size="small" disabled={mutation.isPending} onClick={() => { setFileId(null); setImage(null); setImageError(undefined); }}>Remove avatar</Button>}
						</Stack>
						<Typography variant="caption" color="text.secondary">Optional · PNG, JPEG, GIF or WebP · Max 5 MB</Typography>
						{!canUploadAvatar && <Typography variant="caption" color="text.secondary">File Read and Create permissions are needed to upload an avatar.</Typography>}
					</Stack>
				</Stack>
				{imageError && <Alert severity="error">{imageError}</Alert>}
				<TextField
					autoFocus
					margin="dense"
					label="Name"
					fullWidth
					value={name}
					required
					disabled={mutation.isPending}
					onChange={(event) => setName(event.target.value)}
					onKeyDown={(event) => {
						if (event.key === 'Enter' && !mutation.isPending) {
							event.preventDefault();
							mutation.mutate();
						}
					}}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
				<Button onClick={() => mutation.mutate()} disabled={mutation.isPending || !name.trim()} variant="contained">{isEditing ? 'Save' : 'Create'}</Button>
			</DialogActions>
		</Dialog>
	);
}
